import ApolloClient from "apollo-client";
import {
  AddUserCommentMutation,
  AddUserCommentVariables,
  AddUserMutation,
  AddUserVariables,
  ClinicDataQuery,
  ClinicDataVariables,
  CreateClinicCreateClinic,
  CreateClinicMutation,
  CreateClinicVariables,
  DeleteClinicMutation,
  DeleteClinicVariables,
  DeleteCodeMutation,
  DeleteCodeVariables,
  DelUserMutation,
  DelUserVariables,
  FilteredVisitsFilteredVisits,
  FilteredVisitsQuery,
  FilteredVisitsVariables,
  FilterInput,
  GetClinicConfigData,
  GetClinicConfigQuery,
  GetClinicConfigVariables,
  GetCodeQuery,
  GetCodeVariables,
  GetUserDataQuery,
  GetUserDataUserData,
  GetUserDataVariables,
  GetUsersOfClinicQuery,
  GetUsersOfClinicUsers,
  GetUsersOfClinicVariables,
  IsClinicNameAvailableQuery,
  IsClinicNameAvailableVariables,
  MakeClinicShortCodeMutation,
  MakeClinicShortCodeVariables,
  Order,
  RecentCodesQuery,
  RecentCodesRecentCodes,
  RecentCodesVariables,
  RecentVisitsQuery,
  RecentVisitsRecentVisits,
  RecentVisitsVariables,
  RenameClinicMutation,
  RenameClinicVariables,
  SetClinicConfigMutation,
  SetClinicConfigVariables,
  ShareCodeDataFragmentFragment,
  StartKey,
  UpdateSeenByUserMutation,
  UpdateSeenByUserVariables,
  UserClinicsClinics,
  UserClinicsQuery,
  UserClinicsVariables,
  UserRoles,
  FilteredVisitsVisits,
  GetPoliciesQuery,
  GetPoliciesPolicies,
  GetPanelsQuery,
  GetPanelsPanels,
  GetCodeByIdGetCodeById,
  GetCodeByIdVariables,
  GetCodeByIdQuery,
  ClinicByNameQuery,
  ClinicByNameVariables,
  CopyCaseToClinicClinic,
  CopyCaseToClinicVariables,
  CopyCaseToClinicMutation,
} from "../types";
import { Web } from "../web";
import { BaseDao } from "./baseDao";
import { ClinicConfig, ClinicSolverData } from "../clinic/clinicTypes";

export class ClinicDao extends BaseDao {
  constructor(
    public gqlClient: (cached?: boolean) => Promise<ApolloClient<any>>,
  ) {
    super(gqlClient);
  }

  async createClinic(
    name: string,
    displayName?: string,
    config?: ClinicConfig,
  ): Promise<CreateClinicCreateClinic> {
    const variables: CreateClinicVariables = {
      name,
      displayName,
      config,
    };

    return (
      await this.doMutate<CreateClinicMutation>({
        mutation: Web.CreateClinic,
        variables,
      })
    ).user.createClinic;
  }

  async copyCaseToClinic(
    srcClinicId: string,
    destClinicId: string,
    caseId: string,
  ): Promise<CopyCaseToClinicClinic> {
    const variables: CopyCaseToClinicVariables = {
      clinicId: srcClinicId,
      caseId,
      destClinicId,
    };

    return (
      await this.doMutate<CopyCaseToClinicMutation>({
        mutation: Web.CopyCaseToClinic,
        variables,
      })
    ).clinic;
  }

  async getClinicConfig(clinic: string): Promise<GetClinicConfigData> {
    const variables: GetClinicConfigVariables = {
      id: clinic,
    };

    return (
      await this.doQuery<GetClinicConfigQuery>({
        query: Web.GetClinicConfig,
        variables,
      })
    )?.clinic?.data;
  }

  async makeClinicShortCode(clinic: string, shareId: string): Promise<string> {
    const variables: MakeClinicShortCodeVariables = {
      id: clinic,
      shareid: shareId,
    };

    const dbCodeRow = await this.doMutate<MakeClinicShortCodeMutation>({
      mutation: Web.MakeClinicShortCode,
      variables,
    });

    return (
      dbCodeRow?.clinic?.updateFinished?.digitCode ||
      dbCodeRow?.clinic?.updateFinished?.id
    );
  }

  async recentCodes(
    id: string,
    daysPrior: number,
    limit: number,
    isFinished = true,
    lastItem: { id: string; date: number } = null,
  ): Promise<RecentCodesRecentCodes[]> {
    const variables: RecentCodesVariables = {
      daysPrior,
      id,
      limit,
      isFinished,
      exclusiveStartDate: lastItem?.date || undefined,
      exclusiveStartId: lastItem?.id || undefined,
    };

    const res = (
      await this.doQuery<RecentCodesQuery>({
        query: Web.RecentCodes,
        variables,
      })
    ).clinic.recentCodes;

    return res.map((r) => ({
      ...r,
      patientProperties: {
        ...r.visitStatus,
        ...r.patientProperties,
      },
    }));
  }

  async recentVisits(
    id: string,
    limit: number,
    isFinished = true,
    startKey: StartKey = null,
    order: Order = null,
  ): Promise<RecentVisitsRecentVisits> {
    const variables: RecentVisitsVariables = {
      id,
      limit,
      isFinished,
      startKey,
      order,
    };

    const res = (
      await this.doQuery<RecentVisitsQuery>({
        query: Web.RecentVisits,
        variables,
      })
    ).clinic.recentVisits;

    return {
      visits:
        res?.visits.map((r) => ({
          ...r,
          patientProperties: {
            ...r.visitStatus,
            ...r.patientProperties,
          },
        })) || [],
      startKey: res?.startKey,
    };
  }

  async recentVisitsFiltered(
    id: string,
    filter: FilterInput,
    startKey?: StartKey,
    sortKey?: string,
    limitRequested?: number,
    isFinished?: boolean,
    order?: Order,
  ): Promise<FilteredVisitsFilteredVisits> {
    const variables: FilteredVisitsVariables = {
      id,
      filter,
      startKey,
      sortKey,
      limitRequested,
      isFinished,
      order,
    };

    const res = (
      await this.doQuery<FilteredVisitsQuery>({
        query: Web.RecentVisitsFiltered,
        variables,
      })
    ).clinic.filteredVisits;

    return {
      visits:
        res?.visits.map((r) => ({
          ...r,
          patientProperties: {
            ...r.visitStatusStructure,
            ...r.patientProperties,
          },
        })) || [],
      startKey: res?.startKey,
      count: res?.count,
    };
  }

  // async totalVisitsFiltered(id:String,filter: FilterInput, limitRequested: number, isFinished: Boolean): Promise<TotalVisitsFilteredQuery> {
  //   const res
  // };

  async clinicData(id: string): Promise<ClinicSolverData> {
    const variables: ClinicDataVariables = {
      id,
    };

    return (
      await this.doQuery<ClinicDataQuery>({
        query: Web.ClinicData,
        variables,
      })
    ).clinic.data;
  }

  async setClinicConfig(id: string, config: ClinicConfig): Promise<String> {
    const variables: SetClinicConfigVariables = {
      id,
      config,
    };

    return (
      await this.doMutate<SetClinicConfigMutation>({
        mutation: Web.SetClinicConfig,
        variables,
      })
    ).clinic.setConfig.status;
  }

  async userClinics(
    showOnlyUsersClinic = false,
  ): Promise<UserClinicsClinics[]> {
    const variables: UserClinicsVariables = {
      onlyMyClinics: showOnlyUsersClinic,
    };

    return (
      await this.doQuery<UserClinicsQuery>({
        query: Web.UserClinics,
        variables,
      })
    ).user.clinics;
  }

  async getPatientFromCode(
    clinicId: string,
    digitCode: string,
  ): Promise<FilteredVisitsVisits> {
    const variables: GetCodeVariables = { code: digitCode, id: clinicId };

    return (
      await this.doQuery<GetCodeQuery>(
        {
          query: Web.getCode,
          variables,
        },
        false,
      )
    ).clinic.getCodeByDigits as FilteredVisitsVisits; //TODO case by alphanumeric code instead of digits
  }

  async getCodeFromShareId(
    clinicId: string,
    shareId: string,
    ehrid: string = "",
  ): Promise<GetCodeByIdGetCodeById> {
    const variables: GetCodeByIdVariables = { clinicId, shareId, ehrid };

    return (
      await this.doQuery<GetCodeByIdQuery>(
        {
          query: Web.getCodeByShareId,
          variables,
        },
        false,
      )
    ).clinic.getCodeById;
  }

  async addUserToClinic(
    clinicId: string,
    user: string,
    role: UserRoles,
  ): Promise<string> {
    const variables: AddUserVariables = {
      clinicId,
      user,
      role,
    };

    return (
      await this.doMutate<AddUserMutation>({
        mutation: Web.AddUserToClinic,
        variables,
      })
    ).clinic.addUser.status;
  }

  async removeUserFromClinic(clinicId: string, user: string): Promise<string> {
    const variables: DelUserVariables = {
      clinicId,
      user,
    };

    return (
      await this.doMutate<DelUserMutation>({
        mutation: Web.RemoveUserFromClinic,
        variables,
      })
    ).clinic.delUser.status;
  }

  async getUsersOfClinic(id: string): Promise<GetUsersOfClinicUsers[]> {
    const variables: GetUsersOfClinicVariables = {
      id,
    };

    return (
      await this.doQuery<GetUsersOfClinicQuery>({
        query: Web.GetUsersOfClinic,
        variables,
      })
    ).clinic.users;
  }

  async getCurrentClinicUser(id: string): Promise<GetUserDataUserData> {
    const variables: GetUserDataVariables = {
      id,
    };

    return (
      await this.doQuery<GetUserDataQuery>({
        query: Web.GetUserData,
        variables,
      })
    ).clinic.userData;
  }

  async deleteClinic(id: string): Promise<string> {
    const variables: DeleteClinicVariables = {
      id,
    };

    return (
      await this.doMutate<DeleteClinicMutation>({
        mutation: Web.deleteClinic,
        variables,
      })
    ).clinic.deleteClinic.status;
  }

  async deleteCode(clinicId: string, codeId: string): Promise<string> {
    const mutation = Web.deleteCode;

    const variables: DeleteCodeVariables = {
      clinicId,
      code: codeId,
    };

    return (await this.doMutate<DeleteCodeMutation>({ mutation, variables }))
      .clinic.deleteCode.status;
  }

  async addUserComment(
    clinicId: string,
    digitCode: string,
    comment: string,
  ): Promise<string> {
    const mutation = Web.addUserComment;

    const variables: AddUserCommentVariables = {
      clinicId,
      code: digitCode,
      comment,
    };

    return (
      await this.doMutate<AddUserCommentMutation>({ mutation, variables })
    ).clinic.addUserComment.status;
  }

  async updateSeenStatus(
    clinicId: string,
    patientCode: string,
    userId: string,
    isSeen: boolean,
  ): Promise<ShareCodeDataFragmentFragment> {
    const mutation = Web.MarkCodeAsSeen;

    const variables: UpdateSeenByUserVariables = {
      clinicId,
      user: userId,
      code: patientCode,
      isSeen,
    };

    return (
      await this.doMutate<UpdateSeenByUserMutation>({ mutation, variables })
    ).clinic.updateSeenByUser;
  }

  async renameClinic(
    clinicId: string,
    clinicUrlName: string,
    clinicDisplayName: string,
  ): Promise<string> {
    const mutation = Web.RenameClinic;

    const variables: RenameClinicVariables = {
      newName: clinicUrlName,
      id: clinicId,
      newDisplayName: clinicDisplayName,
    };

    return (await this.doMutate<RenameClinicMutation>({ mutation, variables }))
      .clinic.renameClinic.status;
  }

  async isClinicWebNameAvailable(offeredName: string): Promise<boolean> {
    const query = Web.isClinicNameAvailable;

    const variables: IsClinicNameAvailableVariables = {
      name: offeredName,
    };

    return (
      await this.doQuery<IsClinicNameAvailableQuery>({ query, variables })
    ).user.isClinicNameAvailable;
  }

  async getPolicies(): Promise<GetPoliciesPolicies[]> {
    return (
      await this.doQuery<GetPoliciesQuery>({
        query: Web.GetPolicies,
      })
    ).policies;
  }

  async getPanels(): Promise<GetPanelsPanels[]> {
    return (
      await this.doQuery<GetPanelsQuery>({
        query: Web.GetPanels,
      })
    ).panels;
  }

  async getClinicByName(name: string): Promise<ClinicSolverData> {
    const variables: ClinicByNameVariables = {
      name,
    };

    return (
      await this.doQuery<ClinicByNameQuery>({
        query: Web.ClinicByName,
        variables,
      })
    ).clinicByName.data;
  }
}
